import { useState, useCallback, useRef, useEffect } from 'react'
import { X, Upload, Loader2, CheckCircle2, AlertCircle, Info } from 'lucide-react'
import { DIALOG_WIDTH } from '@/components/ui/dialogSize'
import { toast } from 'sonner'
import {
  Dialog, DialogContent, DialogTitle, DialogDescription,
} from '@/components/ui/dialog'
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select'
import { Checkbox } from '@/components/ui/checkbox'
import { IntuiButton } from '@/components/intueri/IntuiButton'
import { Label } from '@/components/ui/label'
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '@/components/ui/tooltip'
import { useAppStore } from '@/store'
import { isFileTooLarge, MAX_FILE_SIZE_MB } from '@/utils/fileValidation'
import { countPdfPages, multiPagePdfProperties } from '@/utils/pdfPageCount'
import type { DocumentType, DocumentData } from '@/store/dataCatalogSlice'
import { apiService } from '@/service/api'
import { uploadFileWithProgress } from '@/service/uploadFileWithProgress'
import { buildOptimisticDocument } from './optimisticDocument'
import { registerPendingBatch, discardPendingUploads } from './pendingUploadBatch'
import RegionSelectionModal from '../RegionSelectionModal'
import { cleanBboxes, pageMapProperties, type UploadBbox } from './regionUpload'
import DuplicateFileModal from '../DuplicateFileModal'
import BulkDuplicateModal, { type DuplicateFile } from '../BulkDuplicateModal'
import { toSelectableFileType, type Confidence } from '@/components/UnifiedFileUpload/fileTypeDetection'
import { detectBatchFileTypesFromContent } from '@/components/UnifiedFileUpload/contentTypeDetection'
import { ConfidenceBadge } from '@/components/UnifiedFileUpload/ConfidenceBadge'
import { useApiFeatureFlag } from '@/hooks/useFeatureFlag'
import { useTranslation } from '@/i18n'
import { rasterizeSldPages, registerSldPages } from '@/service/sldUpload'

/* ── Data-type options matching the V1 upload modal ─────────────── */
// P&ID / PFD stay English per the i18n glossary (labelKey omitted); Document /
// Time Series resolve labelKey with t() at render time, falling back to label.
const DATA_TYPE_OPTIONS: { value: string; label: string; labelKey?: string; formats: string }[] = [
  { value: 'pnid', label: 'P&ID', formats: 'pdf, png, jpg' },
  { value: 'pfd', label: 'PFD', formats: 'pdf, png, jpg' },
  // SLD (VISN — SLD annotation). No parser: pages are rendered in the browser
  // and the annotator draws every object by hand. Stays English like P&ID/PFD.
  { value: 'sld', label: 'SLD', formats: 'pdf, png, jpg' },
  { value: 'document', label: 'Document', labelKey: 'data_catalog:document_option', formats: 'pdf, docx, pptx, txt, csv, xlsx' },
  { value: 'timeseries', label: 'Time Series', labelKey: 'data_catalog:timeseries_option', formats: 'csv, xlsx' },
  // PLC program exports (VISN-305), flag-gated server-side; xml requires
  // this explicit type choice (never auto-routed).
  { value: 'plc_program', label: 'PLC Program', labelKey: 'data_catalog:plc_program_option', formats: 'xml, TcPOU, st, scl, L5X' },
]

/** Share of the progress bar given to rendering an SLD's pages, before upload. */
const RASTER_PROGRESS_CEILING = 40

const ACCEPT_MAP: Record<string, string> = {
  pnid: '.pdf,.png,.jpg,.jpeg,.tiff',
  pfd: '.pdf,.png,.jpg,.jpeg,.tiff',
  // No .tiff: no browser can decode it into a canvas, so it could never be
  // rendered into page rasters — accepting it only produced a document that
  // failed after upload.
  sld: '.pdf,.png,.jpg,.jpeg',
  document: '.pdf,.docx,.pptx,.txt,.csv,.xlsx,.xls',
  timeseries: '.csv,.xlsx,.xls',
  plc_program: '.xml,.tcpou,.st,.scl,.l5x',
}

/** Map UI type → API DocumentType. */
const toApiType = (uiType: string): DocumentType => uiType as DocumentType

/* ── Per-file tracking ───────────────────────────────────────────── */
type FileStatus = 'pending' | 'uploading' | 'complete' | 'error'


/** Pause between the last upload finishing and the modal closing itself, so the finished ticks register. */
const AUTO_CLOSE_DELAY_MS = 800

/** What the duplicate-file modal decided for this entry, if anything. */
type DuplicateDecision = 'replace' | 'keep_both' | undefined

interface FileEntry {
  id: string
  file: File
  name: string
  status: FileStatus
  progress: number
  /** Result of the pre-upload duplicate check + user choice. Drives the
   *  autoRename / replaceDocumentId flags passed to uploadFileWithProgress. */
  duplicateDecision?: DuplicateDecision
  /** Existing document id surfaced by the duplicate check — used when
   *  duplicateDecision === 'replace' to tell the server which doc to swap. */
  existingDocumentId?: string
}

/** Collected file with region bboxes (used in multi-page collect-first flow). */
interface CollectedFile {
  /** The file-list entry this came from, so its row is updated by id, not by name. */
  entryId?: string
  file: File
  name: string
  description: string
  type: string
  bboxes: UploadBbox[] | null
}

/** Whether a file-list row is the given entry: by id when known, else by name. */
const isEntry = (f: { id: string; name: string }, entryId: string | null | undefined, name: string) =>
  entryId ? f.id === entryId : f.name === name

/* ── Props ───────────────────────────────────────────────────────── */
interface AddDataModalProps {
  isOpen: boolean
  onClose: () => void
  initialFiles?: File[]
}

export default function AddDataModal({ isOpen, onClose, initialFiles }: AddDataModalProps) {
  const { t } = useTranslation()
  // PLC ingestion is flag-gated end to end (VISN-305). Default OFF while the
  // check is in flight so flag-off tenants never see (or get auto-routed into)
  // a pipeline whose worker would reject the job.
  const { enabled: plcProgramsEnabled } = useApiFeatureFlag('plc_program_ingestion_enabled', false)
  // SLD page images at P&ID scale ride with the SLD parse (VISN-304): until it
  // ships, annotation uploads render exactly as they always have.
  const { enabled: sldParsingEnabled } = useApiFeatureFlag('sld_parsing_enabled', false)
  const dataTypeOptions = DATA_TYPE_OPTIONS.filter(
    (opt) => opt.value !== 'plc_program' || plcProgramsEnabled,
  )
  const [selectedType, setSelectedType] = useState('')
  const [files, setFiles] = useState<FileEntry[]>([])
  const [isDragging, setIsDragging] = useState(false)
  const [isMultiPage, setIsMultiPage] = useState(false)
  // Default ON: skip region selection for P&ID/PFD uploads (regions can be
  // assigned later) — faster default upload flow per product request.
  const [bypassAreaSelection, setBypassAreaSelection] = useState(true)
  // FS-529: opt-in P&ID parsing for mixed-document (PDF) uploads. OFF by
  // default — when off, the mixed-document worker bypasses P&ID extraction.
  const [parsePnids, setParsePnids] = useState(false)

  // Region selection state
  const [regionQueue, setRegionQueue] = useState<FileEntry[]>([])
  const [currentRegionFile, setCurrentRegionFile] = useState<DocumentData | null>(null)
  const [isRegionModalOpen, setIsRegionModalOpen] = useState(false)
  // The file-list entry the region modal is showing. Its row is updated by this
  // id: two queued drawings can share a filename, and updating by name marked
  // both finished when the first one was (Greptile on #36901).
  const currentRegionEntryIdRef = useRef<string | null>(null)

  // Multi-page collect-first: gather all files+bboxes before uploading
  const [collectedFiles, setCollectedFiles] = useState<CollectedFile[]>([])

  // Duplicate-file modal state — pre-upload check via apiService.checkDuplicateDocument.
  // Single-file collision: DuplicateFileModal. Multi-file: BulkDuplicateModal.
  const [isCheckingDuplicates, setIsCheckingDuplicates] = useState(false)
  const [showSingleDupModal, setShowSingleDupModal] = useState(false)
  const [showBulkDupModal, setShowBulkDupModal] = useState(false)
  const [singleDupName, setSingleDupName] = useState('')
  const [bulkDupFiles, setBulkDupFiles] = useState<DuplicateFile[]>([])
  const [newFilesCount, setNewFilesCount] = useState(0)
  // Confidence badge for an auto-detected data type (cleared on manual pick)
  const [detectedConfidence, setDetectedConfidence] = useState<Confidence | null>(null)
  const autoDetectRanRef = useRef(false)
  // After the user picks Replace / Keep Both / Skip, this guard prevents the
  // auto-upload effect from re-triggering the duplicate check on the same
  // batch (entries now carry their decision so the check is a no-op).
  const duplicatesResolvedRef = useRef(false)

  const fileInputRef = useRef<HTMLInputElement>(null)
  const dragCountRef = useRef(0)
  /**
   * Entry ids already handed to an upload run. A run claims its entries
   * SYNCHRONOUSLY, before its first await, so the effect that starts it can
   * re-fire — on a later `files` change, or for a second batch queued while
   * this one is still going — without uploading anything twice. This replaces
   * a single `uploadingRef` boolean, which could only express "an upload is
   * happening somewhere" and so had to block the second batch outright.
   */
  const claimedEntryIdsRef = useRef<Set<string>>(new Set())

  const regionStartedRef = useRef(false)
  const initialFilesProcessedRef = useRef(false)
  const { fetchDocuments, uploadDocument, prependDocument, updateDocument, removeDocument, user } = useAppStore()

  /**
   * Close the modal on its own once its uploads are done, after a short pause
   * so the finished ticks register (the behaviour before #31920 / UI-11, which
   * left it waiting for Done). An upload run arms it when it finishes; from
   * then on the modal closes as soon as every file has uploaded, which also
   * covers region-selection uploads that are still going behind the region
   * modal when it finishes. It stays open while any file failed, so the error
   * is visible, or is still uploading or waiting.
   */
  const [autoCloseArmed, setAutoCloseArmed] = useState(false)
  const closeWhenFinished = useCallback(() => setAutoCloseArmed(true), [])
  // Read through a ref so a parent re-render (a new onClose) cannot keep
  // restarting the pause.
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose
  // A picker opened and cancelled adds nothing: re-arm, so a finished modal
  // still closes. (The input's `cancel` event; React has no prop for it.)
  useEffect(() => {
    const input = fileInputRef.current
    if (!input || !isOpen) return
    const rearm = () => setAutoCloseArmed(true)
    input.addEventListener('cancel', rearm)
    return () => input.removeEventListener('cancel', rearm)
  }, [isOpen])
  useEffect(() => {
    if (!autoCloseArmed || !isOpen || files.length === 0) return
    const allUploaded = files.every((f) => f.status === 'complete')
    if (!allUploaded) return
    const timer = setTimeout(() => onCloseRef.current(), AUTO_CLOSE_DELAY_MS)
    return () => clearTimeout(timer)
  }, [autoCloseArmed, isOpen, files])

  // Reset on close
  useEffect(() => {
    if (!isOpen) {
      setSelectedType('')
      setFiles([])
      setIsDragging(false)
      setIsMultiPage(false)
      setBypassAreaSelection(true)
      setParsePnids(false)
      setRegionQueue([])
      setCurrentRegionFile(null)
      duplicatesResolvedRef.current = false
      setShowSingleDupModal(false)
      setShowBulkDupModal(false)
      setSingleDupName('')
      setBulkDupFiles([])
      setNewFilesCount(0)
      setIsRegionModalOpen(false)
      setCollectedFiles([])
      dragCountRef.current = 0
      // `claimedEntryIdsRef` is deliberately NOT reset here. Closing the modal
      // does not cancel an upload any more — it detaches the user from it — and
      // a run still in flight owns its claims until it finishes with them.
      regionStartedRef.current = false
      initialFilesProcessedRef.current = false
      setDetectedConfidence(null)
      autoDetectRanRef.current = false
      setAutoCloseArmed(false)
      currentRegionEntryIdRef.current = null
    }
  }, [isOpen])

  // Reset the "Parse P&ID diagrams" toggle whenever the data type changes, so
  // switching away from "document" and back always starts with parsing OFF
  // (mirrors V1, and avoids silently opting a later upload into the parse).
  useEffect(() => {
    setParsePnids(false)
  }, [selectedType])

  // Smart file-type detection (roadmap §2): when page drag-drop delivered
  // files (FS-530) and no data type is chosen yet, suggest one from file
  // CONTENT (fails open to filename heuristics) instead of parking the files
  // behind a manual pick. Setting the type fires the ingest effect below.
  // A manual selection during the sniff wins: changing selectedType runs the
  // cleanup, which cancels the pending apply. Runs once per modal open.
  useEffect(() => {
    if (!isOpen || autoDetectRanRef.current) return
    if (!initialFiles || initialFiles.length === 0 || selectedType) return
    autoDetectRanRef.current = true
    let cancelled = false
    void (async () => {
      const detection = await detectBatchFileTypesFromContent(initialFiles)
      if (cancelled || !detection.dominantType) return
      const suggested = toSelectableFileType(detection.dominantType)
      // Flag-off tenants must not be auto-routed into the PLC pipeline (the
      // worker rejects the job) — leave the type unset for a manual choice.
      if (suggested === 'plc_program' && !plcProgramsEnabled) return
      setSelectedType(suggested)
      setDetectedConfidence(detection.files[0]?.confidence ?? null)
    })()
    return () => { cancelled = true }
  }, [isOpen, initialFiles, selectedType, plcProgramsEnabled])

  // When user selects a type and we have initialFiles waiting, validate and add them
  useEffect(() => {
    if (!isOpen || !selectedType || !initialFiles || initialFiles.length === 0) return
    if (initialFilesProcessedRef.current) return
    initialFilesProcessedRef.current = true

    const accepted = ACCEPT_MAP[selectedType]
    if (!accepted) return
    const exts = accepted.split(',').map(e => e.replace('.', '').toLowerCase())
    const validFiles: FileEntry[] = []
    for (const file of initialFiles) {
      const ext = file.name.split('.').pop()?.toLowerCase() || ''
      if (!exts.includes(ext)) {
        toast.error(t('data_catalog:add_data.unsupported_file_type_for', { fileName: file.name, type: selectedType, defaultValue: '{{fileName}}: unsupported file type for {{type}}' }))
        continue
      }
      if (isFileTooLarge(file)) {
        toast.error(t('data_catalog:add_data.file_exceeds_limit', { fileName: file.name, maxSize: MAX_FILE_SIZE_MB, defaultValue: '{{fileName}}: exceeds {{maxSize}}MB limit' }))
        continue
      }
      validFiles.push({ id: crypto.randomUUID(), file, name: file.name, status: 'pending', progress: 0 })
    }
    if (validFiles.length > 0) setFiles(prev => [...prev, ...validFiles])
  }, [isOpen, selectedType, initialFiles, t])

  /* ── File validation & addition ─────────────────────────────── */
  const addFiles = useCallback((newFiles: File[]) => {
    const accepted = ACCEPT_MAP[selectedType]
    if (!accepted) return

    const exts = accepted.split(',').map(e => e.replace('.', '').toLowerCase())
    const validFiles: FileEntry[] = []

    for (const file of newFiles) {
      const ext = file.name.split('.').pop()?.toLowerCase() || ''
      if (!exts.includes(ext)) {
        toast.error(t('data_catalog:add_data.unsupported_file_type_for', { fileName: file.name, type: selectedType, defaultValue: '{{fileName}}: unsupported file type for {{type}}' }))
        continue
      }
      if (isFileTooLarge(file)) {
        toast.error(t('data_catalog:add_data.file_exceeds_limit', { fileName: file.name, maxSize: MAX_FILE_SIZE_MB, defaultValue: '{{fileName}}: exceeds {{maxSize}}MB limit' }))
        continue
      }
      validFiles.push({
        id: crypto.randomUUID(),
        file,
        name: file.name,
        status: 'pending',
        progress: 0,
      })
    }

    if (validFiles.length > 0) {
      setFiles(prev => [...prev, ...validFiles])
    }
  }, [selectedType, t])

  const removeFile = useCallback((id: string) => {
    setFiles(prev => prev.filter(f => f.id !== id))
  }, [])

  /* ── Direct XHR upload (bypass path & non-P&ID types) ─────────── */
  const processUploads = useCallback(async () => {
    // Claim before the first await. `files` entries stay 'pending' until the
    // registration below resolves, so without a synchronous claim the effect
    // would re-enter during that window and upload the batch twice.
    const pending = files.filter(
      f => f.status === 'pending' && !claimedEntryIdsRef.current.has(f.id),
    )
    if (pending.length === 0) return
    pending.forEach(f => claimedEntryIdsRef.current.add(f.id))

    const apiType = toApiType(selectedType)

    // Build multi-page properties if applicable (P&ID + multi-page checked + >1 file)
    const shouldGroupMultiPage = selectedType === 'pnid' && isMultiPage && pending.length > 1
    const multiPageSetId = shouldGroupMultiPage
      ? (typeof crypto !== 'undefined' && 'randomUUID' in crypto
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`)
      : undefined
    const multiPageSetName = shouldGroupMultiPage
      ? pending[0].name.replace(/\s*\(\d+\)\s*/, '').replace(/\.[^.]+$/, '')
      : undefined
    const sheetCount = shouldGroupMultiPage ? String(pending.length) : undefined

    // Properties that are known without awaiting anything. The per-file page
    // count below needs an await, so it rides on the upload instead — the claim
    // merges the upload's properties over the registered row's.
    const registrationProperties: Record<string, unknown> = {}
    if (selectedType === 'document') registrationProperties.parse_pnids = parsePnids

    // A Replace is NOT pre-registered.
    //
    // `/register-pending` deletes the document being replaced at REGISTRATION
    // time, to free its name for the pending row. Registering the batch up
    // front would therefore delete every replaced original before the serial
    // loop below has uploaded any of them — so on a 30-file replace batch,
    // closing the tab after file 3 leaves 27 originals deleted and their
    // replacements never uploaded. The legacy per-file path keeps that delete
    // inside the one request that also stores the bytes, which is a window of
    // a single upload rather than a whole batch, so replaces keep taking it.
    // They get their catalog row on completion, as before.
    const isReplaceEntry = (entry: FileEntry): boolean =>
      entry.duplicateDecision === 'replace' && !!entry.existingDocumentId
    const registrable = pending
      .map((entry, idx) => ({ entry, idx }))
      .filter(({ entry }) => !isReplaceEntry(entry))

    // Pre-register the rest so every queued file has a catalog row NOW, not
    // when its turn in this serial loop finishes (FS-499). The last file of a
    // 30-file P&ID batch used to show nothing for the minutes its predecessors
    // took. Best-effort: `registered: false` falls back to the old post-upload
    // row, because a registration outage must not cost the user their upload.
    const batch = await registerPendingBatch(registrable.map(({ entry, idx }) => ({
      filename: entry.name,
      fileType: apiType,
      fileSize: entry.file.size,
      properties: shouldGroupMultiPage
        ? {
            ...registrationProperties,
            multi_page_set_id: multiPageSetId!,
            sheet_index: String(idx),
            sheet_count: sheetCount!,
            multi_page_set_name: multiPageSetName!,
          }
        : registrationProperties,
      autoRename: true,
    })))

    // Re-align the registration results onto `pending`'s indices, since the
    // replaces were skipped. A mis-aligned docId would claim another file's row.
    const docIdByIndex: (string | undefined)[] = pending.map(() => undefined)
    const nameByIndex: (string | undefined)[] = pending.map(() => undefined)
    registrable.forEach(({ idx }, i) => {
      docIdByIndex[idx] = batch.docIds[i]
      nameByIndex[idx] = batch.filenames[i]
    })

    // One stamp for the batch rather than one per file. The server stamps every
    // pre-registered row at registration time, so a per-file stamp taken later
    // in the loop would disagree with the row it names; sharing the batch's
    // start also keeps a multi-file upload sorted as the one action it was.
    const batchStartedAt = new Date().toISOString()

    pending.forEach((entry, idx) => {
      const docId = docIdByIndex[idx]
      if (!docId) return
      prependDocument(buildOptimisticDocument({
        id: docId,
        // The name the server stored, not the one we sent — registration
        // resolves Keep Both and intra-batch collisions, so these differ.
        name: nameByIndex[idx] ?? entry.name,
        type: apiType,
        uploadStartedAt: batchStartedAt,
        fileSize: entry.file.size,
        userId: user?.userId,
        uploaderName: user?.name || user?.email || null,
        tenantId: user?.tenant_id,
        // What the server wrote on this row; CatalogTableV2 reads it to render
        // the row as present-but-not-yet-actionable.
        status: 'uploading',
        properties: shouldGroupMultiPage ? {
          multi_page_set_id: multiPageSetId!,
          sheet_index: String(idx),
          sheet_count: sheetCount!,
          multi_page_set_name: multiPageSetName!,
        } : undefined,
      }))
    })

    /** Pending rows whose upload never landed — discarded after the loop. */
    const unclaimedDocIds: string[] = []

    for (let i = 0; i < pending.length; i++) {
      const entry = pending[i]
      const docId = docIdByIndex[i]
      setFiles(prev => prev.map(f =>
        f.id === entry.id ? { ...f, status: 'uploading' as FileStatus, progress: 0 } : f
      ))

      // Build upload properties: multi-page grouping (P&ID) and/or the FS-529
      // opt-in P&ID-parsing flag for mixed-document (PDF) uploads.
      const uploadProperties: Record<string, unknown> = {}
      if (shouldGroupMultiPage) {
        uploadProperties.multi_page_set_id = multiPageSetId!
        uploadProperties.sheet_index = String(i)
        uploadProperties.sheet_count = sheetCount!
        uploadProperties.multi_page_set_name = multiPageSetName!
      }
      if (selectedType === 'document') {
        // OFF by default; the mixed-document worker reads properties.parse_pnids
        // and skips CASCADE classification + per-page P&ID parsing when false.
        uploadProperties.parse_pnids = parsePnids
      }
      // The region modal is the only other writer of total_pdf_pages, so a
      // multi-page P&ID PDF uploaded with "Bypass Area Selection" (the
      // default) would silently process page 1 only. Count pages here so the
      // backend fans out one job per page. Grouped sets keep their
      // one-sheet-per-file semantics.
      if ((apiType === 'pnid' || apiType === 'pfd') && !shouldGroupMultiPage) {
        const multiPageProps = multiPagePdfProperties(await countPdfPages(entry.file))
        if (multiPageProps) Object.assign(uploadProperties, multiPageProps)
      }
      const uploadProps = Object.keys(uploadProperties).length > 0
        ? { properties: uploadProperties }
        : undefined

      try {
        // Carry the user's duplicate-modal choice (if any) into the upload.
        // Default behavior — no decision recorded — keeps the legacy
        // autoRename=true so manual upload requests without a collision
        // still de-dupe server-side.
        const isReplace = entry.duplicateDecision === 'replace'

        // Render an SLD's pages BEFORE uploading it.
        //
        // An SLD document with no registered pages can never be opened and has
        // no recovery path, so a render failure must happen while there is still
        // nothing to orphan. Ordering is the fix here, not a catch block.
        const sldPages =
          apiType === 'sld'
            ? await rasterizeSldPages(entry.file, {
                pnidScale: sldParsingEnabled,
                onProgress: (done, total) => {
                  setFiles(prev => prev.map(f =>
                    f.id === entry.id
                      ? { ...f, progress: Math.round((done / total) * RASTER_PROGRESS_CEILING) }
                      : f
                  ))
                },
              })
            : undefined

        // Shared with the row prepended above so their upload_started_at match
        // exactly (consistent sort + clean reconcile on the post-upload
        // refetch).
        const uploadStartedAt = batchStartedAt
        const uploadResult = await uploadFileWithProgress(
          entry.file,
          apiType,
          (percent) => {
            // `uploadFileWithProgress` reports 0-90. When a rasterize pass ran
            // first, compress that into the band ABOVE it so the bar cannot run
            // backwards — it used to jump 90% → 62% the moment registration
            // started.
            const shown = sldPages
              ? RASTER_PROGRESS_CEILING +
                Math.round((percent / 90) * (100 - RASTER_PROGRESS_CEILING - 10))
              : percent
            setFiles(prev => prev.map(f =>
              f.id === entry.id ? { ...f, progress: shown } : f
            ))
          },
          undefined,
          {
            ...uploadProps,
            // Claiming a pre-registered row: the name was resolved and the
            // replaced document deleted at registration, and the server ignores
            // `replace_document_id` when a doc_id is present. autoRename must be
            // said explicitly — `uploadFileWithProgress` appends auto_rename
            // unless it is false, so omitting it would have the server probe and
            // suffix a name it had already resolved.
            ...(docId
              ? { docId, autoRename: false }
              : {
                  autoRename: !isReplace,
                  replaceDocumentId: isReplace ? entry.existingDocumentId : undefined,
                }),
            uploadStartedAt,
          },
        )

        // An SLD is not finished when the file lands: it has no parser, so its
        // page rasters and blank per-page graphs are registered here. Until that
        // runs the document cannot be opened, so the row stays 'uploading' and a
        // failure marks it errored rather than letting a green row hide an
        // unannotatable drawing.
        if (apiType === 'sld' && sldPages) {
          if (!uploadResult?.id) {
            // Without an id there is nothing to register against, and marking
            // the row complete would show a green entry over a document that
            // can never be opened.
            throw new Error(`${entry.name}: upload returned no document id`)
          }
          try {
            await registerSldPages(uploadResult.id, sldPages)
          } catch (registerErr) {
            // Remove the document rather than leave an orphan. An SLD whose
            // pages never registered cannot be opened and has no recovery
            // path — the modal's retry re-uploads (creating a second one) and
            // the parse-retry route is annotator-blocked. Deleting returns the
            // catalog to the state before this attempt.
            try {
              await apiService.deleteDocuments([uploadResult.id])
            } catch (cleanupErr) {
              // Report the ORIGINAL failure, not the cleanup's — the cleanup is
              // best-effort and its error would bury the useful one.
              console.error('[sld] could not remove the unregistered document', cleanupErr)
            }
            throw registerErr
          }
        }

        setFiles(prev => prev.map(f =>
          f.id === entry.id ? { ...f, status: 'complete' as FileStatus, progress: 100 } : f
        ))

        // Pre-registered: the catalog row has been on screen since before the
        // bytes went up, so there is nothing to insert — only to move off
        // 'uploading'. The server cleared its own status on claim, which
        // renders as Processing; match that rather than wait for the refetch.
        if (docId) {
          updateDocument(docId, { status: 'processing' })
        } else if (uploadResult?.id) {
          // Legacy path (registration unavailable): FS-555's post-upload row.
          // Keyed on the real server id so the next fetchDocuments() merge
          // dedupes it cleanly. Restores V1's upload-phase behavior (FS-38).
          prependDocument(buildOptimisticDocument({
            id: uploadResult.id,
            name: entry.name,
            type: apiType,
            uploadStartedAt,
            fileSize: entry.file.size,
            userId: user?.userId,
            uploaderName: user?.name || user?.email || null,
            tenantId: user?.tenant_id,
            properties: shouldGroupMultiPage ? {
              multi_page_set_id: multiPageSetId!,
              sheet_index: String(i),
              sheet_count: sheetCount!,
              multi_page_set_name: multiPageSetName!,
            } : undefined,
          }))
        }
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Upload failed'
        setFiles(prev => prev.map(f =>
          f.id === entry.id ? { ...f, status: 'error' as FileStatus, progress: 0 } : f
        ))
        // Take the optimistic row back. Leaving it is not a cosmetic choice:
        // fetchDocuments CARRIES FORWARD un-indexed rows the server omits, so
        // a row left behind is re-inserted by the refetch below and sits at
        // "Uploading" for the whole grace window with no upload behind it.
        if (docId) {
          removeDocument(docId)
          unclaimedDocIds.push(docId)
        }
        toast.error(`${entry.name}: ${msg}`)
      }
    }

    // Soft-delete the pending rows nothing claimed, so they don't linger
    // server-side either (idempotent; already-claimed ids are ignored).
    discardPendingUploads(batch, unclaimedDocIds)

    pending.forEach(f => claimedEntryIdsRef.current.delete(f.id))

    // Refresh catalog in background — don't block the modal UI
    fetchDocuments()

    closeWhenFinished()
  }, [files, selectedType, isMultiPage, parsePnids, sldParsingEnabled, fetchDocuments, prependDocument, updateDocument, removeDocument, user, closeWhenFinished])

  /* ── Multi-page batch upload (after all files collected) ──────── */
  const uploadMultiPageBatch = useCallback(async (collected: CollectedFile[]) => {
    if (collected.length === 0) return

    const setId = typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`
    const multiPageSetName = collected[0].name.replace(/\s*\(\d+\)\s*/, '').replace(/\.[^.]+$/, '')
    const sheetCount = String(collected.length)

    // Same pre-registration as the bypass path: a multi-page set is collected
    // in full before anything uploads, so without this NONE of its sheets exist
    // in the catalog until the first one finishes.
    const batch = await registerPendingBatch(collected.map((c, idx) => ({
      filename: c.name,
      fileType: c.type,
      fileSize: c.file.size,
      properties: {
        multi_page_set_id: setId,
        sheet_index: String(idx),
        sheet_count: sheetCount,
        multi_page_set_name: multiPageSetName,
      },
      autoRename: true,
    })))
    const batchStartedAt = new Date().toISOString()

    collected.forEach((c, idx) => {
      const docId = batch.docIds[idx]
      if (!docId) return
      prependDocument(buildOptimisticDocument({
        id: docId,
        name: batch.filenames[idx] ?? c.name,
        type: c.type,
        uploadStartedAt: batchStartedAt,
        fileSize: c.file.size,
        userId: user?.userId,
        uploaderName: user?.name || user?.email || null,
        tenantId: user?.tenant_id,
        status: 'uploading',
        properties: {
          multi_page_set_id: setId,
          sheet_index: String(idx),
          sheet_count: sheetCount,
          multi_page_set_name: multiPageSetName,
        },
      }))
    })

    const unclaimedDocIds: string[] = []

    for (let i = 0; i < collected.length; i++) {
      const { entryId, file, name, description, type, bboxes } = collected[i]
      const docId = batch.docIds[i]

      // Mark as uploading in the file list
      setFiles(prev => prev.map(f =>
        isEntry(f, entryId, name) ? { ...f, status: 'uploading' as FileStatus, progress: 50 } : f
      ))

      try {
        await uploadDocument(file, {
          name,
          description,
          type: type as 'pnid' | 'pfd' | 'document' | 'datasheet' | 'timeseries',
          // A grouped set is one sheet per FILE, so page annotations are
          // dropped here on purpose: leaving them on would make the backend
          // additionally fan a member out per page and break that contract.
          // The bypass path skips its page count for grouped sets for the
          // same reason. Only the single-file path above carries the map.
          bboxes: bboxes?.map(({ page: _page, ...box }) => box) ?? null,
          properties: {
            multi_page_set_id: setId,
            sheet_index: String(i),
            sheet_count: sheetCount,
            multi_page_set_name: multiPageSetName,
          },
        }, {
          skipToast: true,
          uploadStartedAt: batchStartedAt,
          // Claiming: the name was resolved at registration, so autoRename
          // would only re-probe it.
          ...(docId ? { docId } : { autoRename: true }),
        })

        setFiles(prev => prev.map(f =>
          isEntry(f, entryId, name) ? { ...f, status: 'complete' as FileStatus, progress: 100 } : f
        ))
        if (docId) updateDocument(docId, { status: 'processing' })
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Upload failed'
        setFiles(prev => prev.map(f =>
          isEntry(f, entryId, name) ? { ...f, status: 'error' as FileStatus, progress: 0 } : f
        ))
        if (docId) {
          removeDocument(docId)
          unclaimedDocIds.push(docId)
        }
        toast.error(`${name}: ${msg}`)
      }
    }

    discardPendingUploads(batch, unclaimedDocIds)

    setCollectedFiles([])
    fetchDocuments()
    closeWhenFinished()
  }, [uploadDocument, fetchDocuments, prependDocument, updateDocument, removeDocument, user, closeWhenFinished])

  /** Whether the current type requires region selection. */
  const needsRegionSelection = (selectedType === 'pnid' || selectedType === 'pfd') && !bypassAreaSelection

  /* ── Region selection flow ────────────────────────────────────── */
  const startRegionSelection = useCallback((entries: FileEntry[]) => {
    if (entries.length === 0) return
    const [first, ...rest] = entries
    setRegionQueue(rest)
    setCollectedFiles([])
    currentRegionEntryIdRef.current = first.id
    setCurrentRegionFile({
      name: first.name,
      description: '',
      type: selectedType as DocumentType,
      file: first.file,
    })
    setIsRegionModalOpen(true)
  }, [selectedType])

  /** Advance to the next file in the queue, or finish. */
  const advanceOrFinish = useCallback((
    queue: FileEntry[],
    allCollected: CollectedFile[],
  ) => {
    if (queue.length > 0) {
      // More files → show next in region modal
      const [next, ...rest] = queue
      setRegionQueue(rest)
      currentRegionEntryIdRef.current = next.id
      setCurrentRegionFile({
        name: next.name,
        description: '',
        type: selectedType as DocumentType,
        file: next.file,
      })
    } else if (isMultiPage && allCollected.length > 0) {
      // Multi-page: all collected → batch upload with multi_page_set_id
      setIsRegionModalOpen(false)
      setCurrentRegionFile(null)
      uploadMultiPageBatch(allCollected)
    } else {
      // Single-page: all files already uploaded individually → done
      setIsRegionModalOpen(false)
      setCurrentRegionFile(null)
      fetchDocuments()
      closeWhenFinished()
    }
  }, [selectedType, isMultiPage, uploadMultiPageBatch, fetchDocuments, closeWhenFinished])

  const handleRegionSubmit = useCallback(async (docData: DocumentData) => {
    if (!docData.file) return
    // Taken now: advanceOrFinish below moves the ref on to the next drawing.
    const entryId = currentRegionEntryIdRef.current

    const cleaned = cleanBboxes(docData.bboxes)

    if (isMultiPage) {
      // Multi-page: COLLECT file+bboxes, do NOT upload yet
      const collectedFile: CollectedFile = {
        entryId: entryId ?? undefined,
        file: docData.file,
        name: docData.name,
        description: docData.description,
        type: docData.type,
        bboxes: cleaned,
      }
      const newCollected = [...collectedFiles, collectedFile]
      setCollectedFiles(newCollected)
      advanceOrFinish(regionQueue, newCollected)
    } else {
      // Single-page: fire upload in background and advance to next file
      // immediately so the region modal doesn't block the user. Progress
      // is reflected in the file list inside AddDataModal once region
      // selection finishes for all files.
      setFiles(prev => prev.map(f =>
        isEntry(f, entryId, docData.name) ? { ...f, status: 'uploading' as FileStatus, progress: 50 } : f
      ))

      // The page map has to travel with the upload. RegionSelectionModal
      // already worked it out (which pages were drawn on, which were skipped)
      // and hands it back on docData; V2 previously read only the bboxes, so
      // the backend saw a single-page P&ID and processed page 1 alone.
      const pageProps = pageMapProperties(docData.totalPdfPages, docData.pdfPageNumbers)
      const uploadStartedAt = new Date().toISOString()
      const file = docData.file

      // Registration is awaited INSIDE this async block, never before
      // `advanceOrFinish` below — the user moves straight on to the next
      // drawing's regions while this one registers and uploads behind them.
      void (async () => {
        const batch = await registerPendingBatch([{
          filename: docData.name,
          fileType: docData.type,
          fileSize: file.size,
          ...(pageProps ? { properties: pageProps } : {}),
          autoRename: true,
        }])
        const docId = batch.docIds[0]

        // The row goes up the moment the upload starts. This is the path EVERY
        // P&ID takes, and a P&ID can parse for the better part of an hour —
        // previously nothing appeared in the catalog until its bytes had
        // finished going up.
        if (docId) {
          prependDocument(buildOptimisticDocument({
            id: docId,
            name: batch.filenames[0] ?? docData.name,
            type: docData.type,
            uploadStartedAt,
            fileSize: file.size,
            userId: user?.userId,
            uploaderName: user?.name || user?.email || null,
            tenantId: user?.tenant_id,
            status: 'uploading',
          }))
        }

        try {
          const uploadResult = await uploadDocument(file, {
            name: docData.name,
            description: docData.description,
            type: docData.type as 'pnid' | 'pfd' | 'document' | 'datasheet' | 'timeseries',
            bboxes: cleaned,
            ...(pageProps && { properties: pageProps }),
          }, {
            skipToast: true,
            uploadStartedAt,
            ...(docId ? { docId } : { autoRename: true }),
          })

          setFiles(prev => prev.map(f =>
            isEntry(f, entryId, docData.name) ? { ...f, status: 'complete' as FileStatus, progress: 100 } : f
          ))

          if (docId) {
            updateDocument(docId, { status: 'processing' })
          } else if (uploadResult?.id) {
            // FS-555 fallback when registration was unavailable. Without a row
            // the SSE gate — derived from `documents` — never opens, so a
            // refetch that fails to surface the document leaves the user
            // watching nothing for the whole parse.
            prependDocument(buildOptimisticDocument({
              id: uploadResult.id,
              name: docData.name,
              type: docData.type,
              uploadStartedAt,
              fileSize: file.size,
              userId: user?.userId,
              uploaderName: user?.name || user?.email || null,
              tenantId: user?.tenant_id,
            }))
          }
          fetchDocuments()
        } catch (err: unknown) {
          const msg = err instanceof Error ? err.message : 'Upload failed'
          setFiles(prev => prev.map(f =>
            isEntry(f, entryId, docData.name) ? { ...f, status: 'error' as FileStatus, progress: 0 } : f
          ))
          if (docId) {
            removeDocument(docId)
            discardPendingUploads(batch, [docId])
            // `uploadDocument` retries through `retryWithBackoff`, and a claim
            // succeeds only once: if the first attempt claimed the row and
            // stored the bytes but its response was lost, the retry is refused
            // and lands here having actually uploaded. Refetch so the server
            // decides whether the document exists, rather than leaving the row
            // we just removed missing until something else refetches. (The
            // other two paths already refetch at the end of their loop.)
            fetchDocuments()
          }
          toast.error(`${docData.name}: ${msg}`)
        }
      })()

      advanceOrFinish(regionQueue, collectedFiles)
    }
  }, [isMultiPage, collectedFiles, regionQueue, uploadDocument, advanceOrFinish, fetchDocuments, prependDocument, updateDocument, removeDocument, user])

  const handleRegionSkip = useCallback(async () => {
    if (!currentRegionFile) return

    if (isMultiPage) {
      // Multi-page skip: collect file with no bboxes
      const collectedFile: CollectedFile = {
        entryId: currentRegionEntryIdRef.current ?? undefined,
        file: currentRegionFile.file!,
        name: currentRegionFile.name,
        description: '',
        type: currentRegionFile.type,
        bboxes: null,
      }
      const newCollected = [...collectedFiles, collectedFile]
      setCollectedFiles(newCollected)
      advanceOrFinish(regionQueue, newCollected)
    } else {
      // Skip region selection: upload without bboxes, but STILL every page.
      // Dismissing the modal means "no regions", not "only page 1" — and with
      // no bboxes there is nothing to carry page numbers, so the count has to
      // come from the file itself, exactly as the bypass path does. Without
      // this a multi-page P&ID skipped here is parsed as a single sheet.
      const pageCount = await countPdfPages(currentRegionFile.file!)
      handleRegionSubmit({ ...currentRegionFile, bboxes: [], totalPdfPages: pageCount })
    }
  }, [currentRegionFile, isMultiPage, collectedFiles, regionQueue, advanceOrFinish, handleRegionSubmit])

  const handleRegionClose = useCallback(() => {
    setIsRegionModalOpen(false)
    setCurrentRegionFile(null)
    setRegionQueue([])
    setCollectedFiles([])
    regionStartedRef.current = false
  }, [])

  /* ── Duplicate-name pre-check ──────────────────────────────────
   *  Calls apiService.checkDuplicateDocument for each pending file and
   *  opens the Replace / Keep Both modal when collisions are found. Once
   *  the user picks, each entry carries its decision and the auto-upload
   *  effect drops through to the existing region / processUploads path.
   *  Network errors fall back to silent auto-rename (matches
   *  UnifiedUploadModal). */
  const runDuplicateCheck = useCallback(async () => {
    // Same claim filter as the effect that calls this: a file an upload run
    // already owns must not be re-offered for a Replace / Keep Both decision.
    const pending = files.filter(
      f => f.status === 'pending' && !claimedEntryIdsRef.current.has(f.id),
    )
    if (pending.length === 0) return
    setIsCheckingDuplicates(true)
    try {
      const results = await Promise.allSettled(
        pending.map(async (entry) => {
          const r = await apiService.checkDuplicateDocument(entry.file.name, undefined, selectedType || undefined)
          return { entry, exists: r.exists, existingId: r.existing_document?.id ?? null }
        }),
      )
      const fulfilled = results
        .filter((r): r is PromiseFulfilledResult<{ entry: FileEntry; exists: boolean; existingId: string | null }> => r.status === 'fulfilled')
        .map((r) => r.value)
      const duplicates = fulfilled.filter((r) => r.exists && r.existingId)

      if (duplicates.length === 0) {
        // No collisions — autoRename stays the default behavior (true).
        duplicatesResolvedRef.current = true
        setFiles((prev) => prev.map((f) => ({ ...f, duplicateDecision: 'keep_both' })))
        return
      }

      if (duplicates.length === 1 && pending.length === 1) {
        setSingleDupName(duplicates[0].entry.file.name)
        // Remember the existing-doc id on the entry so handleSingleReplace
        // can read it back without re-running the check.
        setFiles((prev) => prev.map((f) =>
          f.id === duplicates[0].entry.id
            ? { ...f, existingDocumentId: duplicates[0].existingId! }
            : f))
        setShowSingleDupModal(true)
        return
      }

      const dupes: DuplicateFile[] = duplicates.map((d) => ({
        fileName: d.entry.file.name,
        existingDocumentId: d.existingId!,
      }))
      setBulkDupFiles(dupes)
      setNewFilesCount(pending.length - duplicates.length)
      setFiles((prev) => prev.map((f) => {
        const dup = duplicates.find((d) => d.entry.id === f.id)
        return dup ? { ...f, existingDocumentId: dup.existingId! } : f
      }))
      setShowBulkDupModal(true)
    } catch {
      // All checks failed — fall back to auto-rename so uploads still go through.
      duplicatesResolvedRef.current = true
      setFiles((prev) => prev.map((f) => ({ ...f, duplicateDecision: 'keep_both' })))
    } finally {
      setIsCheckingDuplicates(false)
    }
  }, [files, selectedType])

  // Auto-start upload/region-selection when pending files appear, gated on
  // the duplicate-name check completing first.
  useEffect(() => {
    // Entries a run has already claimed are NOT up for grabs, even though they
    // still read 'pending' until their registration resolves. Every branch
    // below has to use this list rather than filtering on status alone: the
    // region branch gathering raw 'pending' entries would hand files that
    // processUploads is already uploading to region selection as well, and
    // upload them a second time.
    const unclaimed = files.filter(
      f => f.status === 'pending' && !claimedEntryIdsRef.current.has(f.id),
    )
    if (unclaimed.length === 0) return

    // Phase 1: ask the server whether any pending file collides on name.
    // The flag is reset in startRegionSelection / handleFileInputChange /
    // handleDrop so each new batch re-runs the check.
    if (!duplicatesResolvedRef.current && !isCheckingDuplicates && !showSingleDupModal && !showBulkDupModal) {
      void runDuplicateCheck()
      return
    }
    // Still waiting on the user — keep the upload paused.
    if (showSingleDupModal || showBulkDupModal || isCheckingDuplicates) return

    if (needsRegionSelection) {
      // Guard: only start region selection once per batch
      if (!regionStartedRef.current) {
        regionStartedRef.current = true
        startRegionSelection(unclaimed)
      }
    } else {
      processUploads()
    }
  }, [files, processUploads, needsRegionSelection, startRegionSelection, runDuplicateCheck, isCheckingDuplicates, showSingleDupModal, showBulkDupModal])

  /* ── Duplicate-modal handlers ────────────────────────────────── */
  const finishDuplicateResolution = useCallback(() => {
    duplicatesResolvedRef.current = true
    setShowSingleDupModal(false)
    setShowBulkDupModal(false)
  }, [])

  const handleSingleReplace = useCallback(() => {
    setFiles((prev) => prev.map((f) =>
      f.status === 'pending' ? { ...f, duplicateDecision: 'replace' } : f))
    finishDuplicateResolution()
  }, [finishDuplicateResolution])

  const handleSingleKeepBoth = useCallback(() => {
    setFiles((prev) => prev.map((f) =>
      f.status === 'pending' ? { ...f, duplicateDecision: 'keep_both' } : f))
    finishDuplicateResolution()
  }, [finishDuplicateResolution])

  const handleBulkReplace = useCallback(() => {
    const dupNames = new Set(bulkDupFiles.map((d) => d.fileName))
    setFiles((prev) => prev.map((f) => {
      if (f.status !== 'pending') return f
      return { ...f, duplicateDecision: dupNames.has(f.file.name) ? 'replace' : 'keep_both' }
    }))
    finishDuplicateResolution()
  }, [bulkDupFiles, finishDuplicateResolution])

  const handleBulkKeepBoth = useCallback(() => {
    setFiles((prev) => prev.map((f) =>
      f.status === 'pending' ? { ...f, duplicateDecision: 'keep_both' } : f))
    finishDuplicateResolution()
  }, [finishDuplicateResolution])

  const handleBulkSkip = useCallback(() => {
    const dupNames = new Set(bulkDupFiles.map((d) => d.fileName))
    setFiles((prev) => {
      // Remove duplicates entirely; keep non-duplicate pending entries.
      const kept = prev.filter((f) => !(f.status === 'pending' && dupNames.has(f.file.name)))
      return kept.map((f) => f.status === 'pending' ? { ...f, duplicateDecision: 'keep_both' } : f)
    })
    finishDuplicateResolution()
  }, [bulkDupFiles, finishDuplicateResolution])

  const handleDuplicateCancel = useCallback(() => {
    // Cancel = drop the queue so nothing uploads. Modal closes; user can pick again.
    setFiles((prev) => prev.filter((f) => f.status !== 'pending'))
    finishDuplicateResolution()
  }, [finishDuplicateResolution])

  /* ── Drag & drop handlers ──────────────────────────────────── */
  const handleDragEnter = useCallback((e: React.DragEvent) => {
    setAutoCloseArmed(false)
    e.preventDefault()
    e.stopPropagation()
    dragCountRef.current++
    if (dragCountRef.current === 1) setIsDragging(true)
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    dragCountRef.current--
    if (dragCountRef.current === 0) setIsDragging(false)
  }, [])

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
  }, [])

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    dragCountRef.current = 0
    setIsDragging(false)
    if (!selectedType) {
      toast.error(t('data_catalog:add_data.select_type_toast'))
      return
    }
    const droppedFiles = Array.from(e.dataTransfer.files)
    if (droppedFiles.length > 0) {
      duplicatesResolvedRef.current = false
      addFiles(droppedFiles)
    }
  }, [selectedType, addFiles, t])

  /* ── File input handler ────────────────────────────────────── */
  const handleFileInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files ? Array.from(e.target.files) : []
    if (selected.length > 0) {
      duplicatesResolvedRef.current = false
      addFiles(selected)
    }
    if (fileInputRef.current) fileInputRef.current.value = ''
  }, [addFiles])

  const handleDropZoneClick = useCallback(() => {
    // Adding more files means the user is not done: hold the auto-close. The
    // new files re-arm it when they finish; a cancelled picker re-arms it below
    // (Greptile on #36901: the pending close shut the modal mid-selection).
    setAutoCloseArmed(false)
    fileInputRef.current?.click()
  }, [])

  /*
   * Hide the AddData dialog while region selection is active.
   * Both are Radix Dialogs — keeping both open causes the first dialog's
   * overlay to block interaction with the second.  V1 solves this by
   * closing the upload modal before opening the region modal.
   */
  const dialogVisible = isOpen && !isRegionModalOpen

  // FS-530: page drag-drop opens this modal with `initialFiles`, but the ingest
  // effect above holds them until a data type is chosen (ACCEPT_MAP is keyed by
  // type, so validation can't run sooner). Without feedback the drop looks like
  // it did nothing and the files only "appear" after a type is picked. Surface
  // the queued files + a prompt while we wait for the type selection.
  const awaitingType = !selectedType && !!initialFiles && initialFiles.length > 0

  /* ── Render ────────────────────────────────────────────────── */
  return (
    <>
    <Dialog
      open={dialogVisible}
      onOpenChange={(open) => {
        // Closing mid-upload is allowed: the uploads continue in the
        // background and every file already has its own catalog row to show
        // progress on. The region check stays — a close there is the region
        // modal taking the screen, not the user asking to leave.
        if (!open && !isRegionModalOpen) onClose()
      }}
    >
      <DialogContent className={`${DIALOG_WIDTH.sm} p-0 gap-0`} hideDefaultClose>
        <DialogDescription className="sr-only">{t('data_catalog:add_data.dialog_description', { defaultValue: 'Add data to the data catalog' })}</DialogDescription>
        {/* Header — no X: dismissal is the footer's Done button, which (unlike
            the old X) respects the mid-upload guard the overlay/Escape already
            enforce, and reads unambiguously ("does X cancel my uploads?"). */}
        <div className="flex items-center justify-between px-5 pt-5 pb-3">
          <DialogTitle className="text-base font-semibold">{t('data_catalog:add_data_title')}</DialogTitle>
        </div>

        {/* Body — min-w-0 so this grid child can't grow to a long filename's
            min-content width (DialogContent is a CSS grid; grid items default
            to min-width:auto), which would blow past max-w-[480px] and push the
            file-row text outside the modal. With min-w-0 the inner flex-1
            min-w-0 + truncate chain on each file row constrains correctly. */}
        <div className="px-5 pb-5 flex flex-col gap-4 min-w-0">
          {/* Data Type */}
          <div className="flex flex-col gap-1.5">
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium">
                {t('data_catalog:data_type_label')} <span className="text-red-500">*</span>
              </label>
              {/* Confidence badge — shown while the type is auto-detected */}
              {detectedConfidence && <ConfidenceBadge confidence={detectedConfidence} />}
            </div>
            <Select
              value={selectedType}
              onValueChange={(v) => {
                setSelectedType(v)
                setDetectedConfidence(null) // manual pick — no confidence to show
              }}
            >
              <SelectTrigger className={`h-9 ${awaitingType ? 'ring-2 ring-primary ring-offset-1 border-primary' : ''}`}>
                <SelectValue placeholder={t('data_catalog:add_data.data_type_placeholder')} />
              </SelectTrigger>
              <SelectContent>
                {dataTypeOptions.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    <span className="font-medium">{opt.labelKey ? t(opt.labelKey as never) : opt.label}</span>{' '}
                    <span className="text-muted-foreground">({opt.formats})</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* P&ID / PFD options */}
          {(selectedType === 'pnid' || selectedType === 'pfd') && (
            <div className="flex flex-col gap-2">
              {/* Multi-page checkbox — only for P&ID */}
              {selectedType === 'pnid' && (
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="v2-multi-page"
                    checked={isMultiPage}
                    onCheckedChange={(checked) => setIsMultiPage(checked === true)}
                  />
                  <Label htmlFor="v2-multi-page" className="text-sm font-normal cursor-pointer">
                    {t('data_catalog:add_data.multi_page_label')}
                  </Label>
                </div>
              )}

              {/* Bypass Area Selection checkbox — for P&ID and PFD */}
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="v2-bypass-area"
                  checked={bypassAreaSelection}
                  onCheckedChange={(checked) => setBypassAreaSelection(checked === true)}
                />
                <Label htmlFor="v2-bypass-area" className="text-sm font-normal cursor-pointer">
                  {t('data_catalog:add_data.bypass_area_selection_label')}
                </Label>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Info className="h-4 w-4 text-muted-foreground cursor-help" />
                    </TooltipTrigger>
                    <TooltipContent side="right">
                      {t('data_catalog:bypass_area_selection_tooltip')}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            </div>
          )}

          {/* Document options — FS-529: opt-in P&ID parsing for mixed PDFs. OFF
              by default; direct P&ID/PFD uploads always parse, so this only
              applies to the "Document" type (matches the V1 upload modal). */}
          {selectedType === 'document' && (
            <div className="flex items-center space-x-2">
              <Checkbox
                id="v2-parse-pnids"
                checked={parsePnids}
                onCheckedChange={(checked) => setParsePnids(checked === true)}
              />
              <Label htmlFor="v2-parse-pnids" className="text-sm font-normal cursor-pointer">
                {t('data_catalog:parse_pnids_label')}
              </Label>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info className="h-4 w-4 text-muted-foreground cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent side="right" className="max-w-xs">
                    {t('data_catalog:parse_pnids_help')}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          )}

          {/* Drop zone — visible after type selection (Figma 12074-231496) */}
          {selectedType ? (
            <div
              className={`border-2 border-dashed rounded-lg transition-colors cursor-pointer ${
                isDragging
                  ? 'border-primary bg-primary/5'
                  : 'border-[#e5e5e5] hover:border-[#ccc] dark:border-border'
              } py-10`}
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              onClick={handleDropZoneClick}
            >
              <div className="flex flex-col items-center gap-2 text-muted-foreground">
                <Upload className="size-5" />
                <span className="text-sm">{t('data_catalog:add_data.drop_zone_instruction')}</span>
              </div>
            </div>
          ) : awaitingType ? (
            // FS-530: files arrived via page drag-drop but no type is chosen yet.
            // Show them queued (rather than silently holding them in initialFiles)
            // with a prompt to pick a data type — selecting one runs the ingest
            // effect above and moves them into the upload list.
            <div className="border-2 border-dashed border-primary/40 rounded-lg py-5 px-4 flex flex-col gap-3">
              <span className="text-sm font-medium text-foreground text-center">
                {t('data_catalog:add_data.files_waiting_prompt', {
                  count: initialFiles!.length,
                  defaultValue: '{{count}} file(s) ready — choose a data type to continue',
                })}
              </span>
              <ul className="flex flex-col gap-1.5 max-h-[180px] overflow-y-auto overflow-x-hidden">
                {initialFiles!.map((file, i) => (
                  <li key={`${file.name}-${i}`} className="flex items-center gap-2 min-w-0">
                    <span className="text-sm truncate">{file.name}</span>
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <div className="border-2 border-dashed border-[#e5e5e5] dark:border-border rounded-lg py-10 flex items-center justify-center text-sm text-muted-foreground">
              {t('data_catalog:add_data.select_type_first')}
            </div>
          )}

          {/* Hidden file input */}
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={ACCEPT_MAP[selectedType] || ''}
            onChange={handleFileInputChange}
            className="hidden"
          />

          {/* File progress list — appears below the drop zone once files are picked */}
          {/* UI-83 (Jenna): "wrong icon for ts in the upload modal". Each row
              here carried a hardcoded FileText glyph, whatever the data type —
              so an .xlsx uploaded as Time Series showed a document icon.
              Removed rather than corrected: #30997 had already dropped the
              per-row type icon from the catalog and board tables as redundant
              with the Type column, and it is more redundant here, where every
              row is the one type the user just chose in the field above. */}
          {files.length > 0 && (
            <div className="flex flex-col gap-2 max-h-[240px] overflow-y-auto overflow-x-hidden">
              {files.map((entry) => (
                <div key={entry.id} className="flex items-center gap-2 min-h-[32px] min-w-0">
                  <div className="flex-1 min-w-0 overflow-hidden">
                    <span className="text-sm truncate block">{entry.name}</span>
                    {entry.status === 'uploading' && (
                      <div className="flex items-center gap-2 mt-1">
                        <div className="flex-1 h-1.5 bg-[#e5e5e5] rounded-full overflow-hidden">
                          <div
                            className="h-full bg-primary rounded-full transition-[width] duration-200 ease-linear"
                            style={{ width: `${entry.progress}%` }}
                          />
                        </div>
                        <span className="text-xs tabular-nums text-muted-foreground shrink-0 w-8 text-right">
                          {entry.progress}%
                        </span>
                      </div>
                    )}
                  </div>
                  {entry.status === 'uploading' ? (
                    <Loader2 className="size-4 text-muted-foreground animate-spin shrink-0" />
                  ) : entry.status === 'complete' ? (
                    <CheckCircle2 className="size-4 text-emerald-500 shrink-0" />
                  ) : entry.status === 'error' ? (
                    <AlertCircle className="size-4 text-red-500 shrink-0" />
                  ) : (
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); removeFile(entry.id) }}
                      className="opacity-50 hover:opacity-100 transition-opacity shrink-0"
                      aria-label={`${t('data_catalog:add_data.remove_file_aria')} ${entry.name}`}
                    >
                      <X className="size-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Footer — Done (matches DocumentsModal / other pickers). Never
              disabled: it used to grey out for the whole upload, which with no
              header X and a guarded overlay/Escape left the user with no way
              out of the modal at all until every file had finished. Uploads
              are no longer tied to this dialog being open. */}
          <div className="flex items-center justify-end pt-1">
            <IntuiButton onClick={onClose}>
              {t('common:buttons.done', { defaultValue: 'Done' })}
            </IntuiButton>
          </div>
        </div>
      </DialogContent>
    </Dialog>

    {/* Region selection modal — shown when bypass is unchecked for P&ID/PFD */}
    <RegionSelectionModal
      isOpen={isRegionModalOpen}
      onClose={handleRegionClose}
      onSkip={regionQueue.length > 0 || isMultiPage ? handleRegionSkip : undefined}
      documentData={currentRegionFile}
      onSubmit={handleRegionSubmit}
    />

    {/* Duplicate-name prompt — single-file collision (Figma 12858-210045). */}
    <DuplicateFileModal
      isOpen={showSingleDupModal}
      onClose={handleDuplicateCancel}
      fileName={singleDupName}
      onReplace={handleSingleReplace}
      onKeepBoth={handleSingleKeepBoth}
    />

    {/* Duplicate-name prompt — multi-file collision (Figma 12858-210177). */}
    <BulkDuplicateModal
      isOpen={showBulkDupModal}
      onClose={handleDuplicateCancel}
      duplicateFiles={bulkDupFiles}
      newFilesCount={newFilesCount}
      onReplace={handleBulkReplace}
      onKeepBoth={handleBulkKeepBoth}
      onSkip={handleBulkSkip}
    />
    </>
  )
}
